import { createSelector } from '@app/utils/createSelector'

import type { ApiChatServerSettings } from '@app/packages/chat/rest.types'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { getChatApi } from '@app/store/actions/contexts/chatApi'
import { createReduxSlice } from '@app/store/redux_slice'

import { routingDateSelector } from './routing'

const chatSettingsSlice = createReduxSlice<SliceState<ApiChatServerSettings>>('chat_settings')

/** Whole chat_server settings, cached 30 min so the picker and attachment limits don't refetch on every chat open. */
export const chatSettingsResultSelector = createSelector([chatSettingsSlice.selector, routingDateSelector], (sliceState, date) =>
  createLoadable(c =>
    withCheckedSliceState(c, {
      sliceState,
      slice: chatSettingsSlice,
      date: { date, expiresInMinutes: 30 },
      load: dispatch => dispatch(getChatApi()).getSettings(),
    })
  )
)

export const reactionEmojisResultSelector = mapThunkSelector(chatSettingsResultSelector, settings => settings.reaction_emojis ?? [])

export const chatAttachmentSettingsResultSelector = mapThunkSelector(chatSettingsResultSelector, settings => settings.attachments)

/** Seconds after send during which the sender may still delete (cancel) their message; `null` when the server advertises no window — see FALLBACK_TIMEOUT_SECONDS. */
export const chatMessageCancelTimeoutResultSelector = mapThunkSelector(
  chatSettingsResultSelector,
  settings => settings.messages?.cancel_timeout_seconds ?? FALLBACK_TIMEOUT_SECONDS
)

/** Seconds after send during which the sender may still edit their message; `null` when the server advertises no window. */
export const chatMessageEditTimeoutResultSelector = mapThunkSelector(
  chatSettingsResultSelector,
  settings => settings.messages?.edit_timeout_seconds ?? FALLBACK_TIMEOUT_SECONDS
)

/** No advertised window (server predates the field, or the settings fetch failed) → `null`: don't pre-block the action, allow it anytime and let the server stay the source of truth. */
const FALLBACK_TIMEOUT_SECONDS: number | null = null
